import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, createSelector, on } from '@ngrx/store';
import moment from 'moment';
import { AppState } from '../store';
import {
    DeletePlatformLicensePricing,
    DeletePlatformLicensePricingFailure,
    DeletePlatformLicensePricingSuccess,
    LoadPlatformLicenseActivationsThatUseLicense,
    LoadPlatformLicenseActivationsThatUseLicenseSuccess,
    LoadPlatformLicensePricingsThatHaveNotBeenInvoiced,
    LoadPlatformLicensePricingsThatHaveNotBeenInvoicedFailure,
    LoadPlatformLicensePricingsThatHaveNotBeenInvoicedSuccess,
    LoadPlatformLicenses,
    LoadPlatformLicensesById,
    LoadPlatformLicensesByIdFailure,
    LoadPlatformLicensesByIdSuccess,
    LoadPlatformLicensesFailure,
    LoadPlatformLicensesSuccess,
    PlatformLicenseActionFailure,
    UpsertPlatformLicense,
    UpsertPlatformLicenseFailure,
    UpsertPlatformLicensePricing,
    UpsertPlatformLicensePricingFailure,
    UpsertPlatformLicensePricingSuccess,
    UpsertPlatformLicenseSuccess
} from '../actions/platform-license.actions';
import { PlatformLicenseActivationDTO, PlatformLicenseDTO } from '@iota/iota-management-portal-v4-client';

export interface PlatformLicenseState extends EntityState<PlatformLicenseDTO> {
    loading: boolean;
    loaded: boolean;
    actionLoading: boolean;

    actionError: any;
    loadingError: any;

    pricingIdsThatHaveNotBeenInvoiced: string[];
    platformLicenseActivationsByPlatformLicense: Record<string, PlatformLicenseActivationDTO[]>;
}

function selectPlatformLicenseId(a: PlatformLicenseDTO): string {
    return a.platformLicenseId;
}

function sortPlatformLicenses(a: PlatformLicenseDTO, b: PlatformLicenseDTO): number {
    return moment(b.creationDateTime).toDate().getTime() - moment(a.creationDateTime).toDate().getTime();
}

export const platformLicenseAdapter: EntityAdapter<PlatformLicenseDTO> = createEntityAdapter<PlatformLicenseDTO>({
    selectId: selectPlatformLicenseId,
    sortComparer: sortPlatformLicenses
});

export const initialState: PlatformLicenseState = platformLicenseAdapter.getInitialState({
    loading: false,
    actionLoading: false,
    loaded: false,

    lastCreatedPlatformLicenseId: undefined,

    actionError: undefined,
    loadingError: undefined,

    pricingIdsThatHaveNotBeenInvoiced: [],
    platformLicenseActivationsByPlatformLicense: {}
});

export const platformLicenseReducer = createReducer(
    initialState,
    on(LoadPlatformLicenses, (state) => ({ ...state, loadingError: undefined, loading: true })),
    on(LoadPlatformLicensesSuccess, (state, { platformLicenses }) =>
        platformLicenseAdapter.setAll(platformLicenses || [], { ...state, loading: false, loaded: true })
    ),
    on(LoadPlatformLicensesFailure, (state, { error }) => ({ ...state, loading: false, loadingError: error })),

    on(LoadPlatformLicensesById, (state) => ({ ...state, loading: true })),
    on(LoadPlatformLicensesByIdSuccess, (state, { platformLicense }) =>
        platformLicenseAdapter.upsertOne(platformLicense, { ...state, loading: false, actionLoading: false })
    ),
    on(LoadPlatformLicensesByIdFailure, (state, { error }) => ({ ...state, loading: false, actionLoading: false, loadingError: error })),

    on(UpsertPlatformLicense, UpsertPlatformLicensePricing, DeletePlatformLicensePricing, (state) => ({
        ...state,
        actionError: undefined,
        actionLoading: true
    })),
    on(
        UpsertPlatformLicenseSuccess,
        UpsertPlatformLicensePricingSuccess,
        DeletePlatformLicensePricingSuccess,
        LoadPlatformLicensePricingsThatHaveNotBeenInvoiced,
        (state) => ({
            ...state,
            actionLoading: false
        })
    ),
    on(
        UpsertPlatformLicenseFailure,
        UpsertPlatformLicensePricingFailure,
        DeletePlatformLicensePricingFailure,
        LoadPlatformLicensePricingsThatHaveNotBeenInvoicedFailure,
        PlatformLicenseActionFailure,
        (state, { error }) => ({
            ...state,
            actionLoading: false,
            actionError: error
        })
    ),

    on(LoadPlatformLicensePricingsThatHaveNotBeenInvoicedSuccess, (state, { pricingIdsThatHaveNotBeenInvoiced }) => ({
        ...state,
        actionLoading: false,
        pricingIdsThatHaveNotBeenInvoiced: pricingIdsThatHaveNotBeenInvoiced
    })),

    on(LoadPlatformLicenseActivationsThatUseLicense, (state) => ({ ...state, actionError: undefined, actionLoading: true })),
    on(LoadPlatformLicenseActivationsThatUseLicenseSuccess, (state, { platformLicenseId, platformLicenseActivations }) => {
        const platformLicenseActivationsByPlatformLicense = { ...state.platformLicenseActivationsByPlatformLicense };
        platformLicenseActivationsByPlatformLicense[platformLicenseId] = platformLicenseActivations;
        return { ...state, actionLoading: false, platformLicenseActivationsByPlatformLicense };
    })
);

const { selectAll } = platformLicenseAdapter.getSelectors();

export const platformLicenseState = (state: AppState) => state.platformLicenseState;

export const _platformLicenses = createSelector(platformLicenseState, selectAll);
export const platformLicensesLoading = createSelector(platformLicenseState, (state) => state.loading);
export const platformLicensesLoadingError = createSelector(platformLicenseState, (state) => state.loadingError);
export const platformLicensesLoaded = createSelector(platformLicenseState, (state) => state.loaded);

export const platformLicenseActionLoading = createSelector(platformLicenseState, (state) => state.actionLoading);
export const platformLicenseActionError = createSelector(platformLicenseState, (state) => state.actionError);

export const platformLicensePricingIdsThatHaveNotBeenInvoiced = createSelector(
    platformLicenseState,
    (state) => state.pricingIdsThatHaveNotBeenInvoiced
);

export const platformLicenseActivationsByPlatformLicense = createSelector(
    platformLicenseState,
    (state) => state.platformLicenseActivationsByPlatformLicense
);
